import React from 'react';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';

import { FormattedMessage } from '../../util/reactIntl';
import { NamedLink, ExternalLink, IconExport } from '../../components';

import css from './ListingPage.module.css';

const SectionBreadcrumbs = props => {
  const routeConfiguration = useRouteConfiguration();
  const { config, listing } = props;

  const { categories } = config.categoryConfiguration;
  const { categoryLevel1, categoryLevel2, categoryLevel3, categoryLevel4 } =
    listing.attributes.publicData || {};

  /**
   * **Reusable Hierarchy Lookup Function**
   * - This function navigates through the hierarchy step by step.
   * - Each level is found within the subcategories of the previous level.
   */
  const findCategoryByLevel = (categoryId, categoryList) => {
    if (!categoryId || !categoryList) return null;
    return categoryList.find(c => c.id === categoryId) || null;
  };

  /**
   * **Step-by-Step Lookup**
   * - Using the reusable function for better readability.
   */
  const getCategoryData = (levelId, parentCategory) => {
    return parentCategory && parentCategory.subcategories
      ? findCategoryByLevel(levelId, parentCategory.subcategories)
      : null;
  };

  const categoryDataLevel1 = findCategoryByLevel(categoryLevel1, categories);
  const categoryDataLevel2 = getCategoryData(categoryLevel2, categoryDataLevel1);
  const categoryDataLevel3 = getCategoryData(categoryLevel3, categoryDataLevel2);
  const categoryDataLevel4 = getCategoryData(categoryLevel4, categoryDataLevel3);

  const categoryData = {
    categoryLevel1: categoryDataLevel1,
    categoryLevel2: categoryDataLevel2,
    categoryLevel3: categoryDataLevel3,
    categoryLevel4: categoryDataLevel4,
  };

  // Construct search query strings for each breadcrumb level
  const getSearchQuery = level => {
    switch (level) {
      case 1:
        return createResourceLocatorString(
          'SearchPage',
          routeConfiguration,
          {},
          { pub_categoryLevel1: categoryLevel1 }
        );
      case 2:
        return createResourceLocatorString(
          'SearchPage',
          routeConfiguration,
          {},
          { pub_categoryLevel1: categoryLevel1, pub_categoryLevel2: categoryLevel2 }
        );
      case 3:
        return createResourceLocatorString(
          'SearchPage',
          routeConfiguration,
          {},
          {
            pub_categoryLevel1: categoryLevel1,

            pub_categoryLevel2: categoryLevel2,
            pub_categoryLevel3: categoryLevel3,
          }
        );
      case 4:
        return createResourceLocatorString(
          'SearchPage',
          routeConfiguration,
          {},
          {
            pub_categoryLevel1: categoryLevel1,
            pub_categoryLevel2: categoryLevel2,
            pub_categoryLevel3: categoryLevel3,
            pub_categoryLevel4: categoryLevel4,
          }
        );
      default:
        return '';
    }
  };

  // Create an array of categories with id, name, and search
  const categoriesArray = [
    categoryData.categoryLevel1 && {
      id: categoryData.categoryLevel1.id,
      name: categoryData.categoryLevel1.name,
      search: getSearchQuery(1),
    },
    categoryData.categoryLevel2 && {
      id: categoryData.categoryLevel2.id,
      name: categoryData.categoryLevel2.name,
      search: getSearchQuery(2),
    },
    categoryData.categoryLevel3 && {
      id: categoryData.categoryLevel3.id,
      name: categoryData.categoryLevel3.name,
      search: getSearchQuery(3),
    },
    categoryData.categoryLevel4 && {
      id: categoryData.categoryLevel4.id,
      name: categoryData.categoryLevel4.name,
      search: getSearchQuery(4),
    },
  ].filter(Boolean);

  // Render breadcrumbs
  const renderBreadcrumbs = () => {
    return categoriesArray.map(category => {
      return (
        <span className={css.breadcrumbItem} key={category.id}>
          <ExternalLink className={css.breadcrumbLink} href={category.search}>
            {category.name}
          </ExternalLink>
        </span>
      );
    });
  };

  return (
    <section className={css.sectionBreadcrumbs}>
      <nav className={css.breadcrumbList}>{renderBreadcrumbs()}</nav>
      <div className={css.breadcrumbExploreContainer}>
        <FormattedMessage id="ListingPage.breadcrumbExplore" />
        <NamedLink className={css.breadcrumbExploreLink} name="SearchPage">
          <FormattedMessage id="ListingPage.breadcrumbExploreLink" />
          <IconExport className={css.breadcrumbExploreIcon} />
        </NamedLink>
      </div>
    </section>
  );
};

export default SectionBreadcrumbs;
